import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { apiParams } from '../lib/filters.js';
import { fillFor } from '../lib/patterns.js';
import { layerSegments, ribbonColumns, ribbonExportRows } from '../lib/layers.js';
import { withFormats } from '../lib/table.js';
import { STATUS_STATES } from '../lib/palette.js';
import { hours as formatHours, percent, weekLabel, longDate, count } from '../lib/format.js';

/** The filters this view reads, in the API's names. Anything else in the bar leaves it alone. */
const PARAM_KEYS = ['run_id', 'from', 'weeks', 'dimension', 'entity', 'pipeline'];

/** The ribbon has no project dimension: a project's hours against company capacity would mislead. */
const RIBBON_DIMENSIONS = ['company', 'department', 'service', 'office', 'individual'];

/**
 * The demand ribbon (V-1, Phase 20): weekly hours stacked by layer — non-project, recurring,
 * committed explicit, committed inferred, pipeline — against the capacity line, for the company
 * or for one entity of a dimension. Hover a week for the layer split and what stands above the
 * line; the table below carries the same numbers and is what exports.
 *
 * Everything drawn here comes from `GET /forecast/ribbon`; the component adds no arithmetic of
 * its own beyond stacking the layer values it was given, so what the tooltip shows is what the
 * API said and what the table exports.
 *
 * Layers are drawn as keyed paths (`repeat` on the layer key), so a filter change updates the
 * geometry of the surviving series in place rather than repainting them (PRD 7.3).
 *
 * The marks are rendered by `chart-frame` inside *its* shadow root, so no stylesheet of this
 * component reaches them: every mark carries its presentation as attributes (fill, stroke,
 * font), with tokens as the values. A class here would silently fall back to SVG's defaults —
 * black fills over the whole plot.
 */
class DemandRibbon extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _hover: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._loading = false;
    this._error = null;
    this._hover = null;
    this._tableOpen = true;
    this._requestKey = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .legend-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 22px;
        margin-top: 10px;
      }
      .line-key {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        font-size: 12.5px;
        color: var(--ink);
      }
      .line-key svg {
        display: block;
      }
      .over-key {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      .over-key .word {
        color: var(--ink);
        font-weight: 400;
      }
      .note {
        margin: 10px 0 0;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .state {
        padding: 36px 0;
        text-align: center;
        color: var(--ink-muted);
      }
      .state.error {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      details.table {
        margin-top: 16px;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: var(--link);
        list-style: none;
      }
      details.table summary::-webkit-details-marker {
        display: none;
      }
      details.table summary::before {
        content: '▸ ';
        color: var(--ink-muted);
      }
      details.table[open] summary::before {
        content: '▾ ';
      }
      details.table data-table {
        margin-top: 10px;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    // Fetch off the store, not off the render cycle: a filter or run change asks the API only
    // when a parameter this view reads has moved, and setting the loading state does not
    // schedule a render from inside a render.
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  /** The request this view wants for the current filters, or null when there is no run. */
  _params() {
    const { filters, run } = this.appState;
    if (!run) return null;
    const params = apiParams(filters, PARAM_KEYS);
    if (!RIBBON_DIMENSIONS.includes(params.dimension)) {
      delete params.dimension;
      delete params.entity;
    }
    return params;
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/ribbon', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Drawing

  get _weeks() {
    return this._data?.weeks ?? [];
  }

  get _layers() {
    return this._data?.layers ?? [];
  }

  _maxValue() {
    let max = 0;
    for (const w of this._weeks) max = Math.max(max, w.demand_hours, w.capacity_hours);
    return max;
  }

  /**
   * An area for one layer: its top edge across the weeks, then back along its base. Points sit
   * at week centres and the ends are pulled out to the band edges, so the ribbon spans the plot.
   */
  _areaPath(layerIndex, x, y) {
    const weeks = this._weeks;
    if (!weeks.length) return '';
    const stacks = weeks.map((w) => layerSegments(w.layers)[layerIndex]);
    const xs = weeks.map((w, i) => {
      const centre = x.centre(w.week_start);
      if (i === 0) return x(w.week_start);
      if (i === weeks.length - 1) return x(w.week_start) + x.bandwidth();
      return centre;
    });
    if (weeks.length === 1) {
      const [s] = stacks;
      const x0 = x(weeks[0].week_start);
      const x1 = x0 + x.bandwidth();
      return `M${x0},${y(s.to)} L${x1},${y(s.to)} L${x1},${y(s.from)} L${x0},${y(s.from)} Z`;
    }
    const top = stacks.map((s, i) => `${xs[i]},${y(s.to)}`);
    const base = stacks.map((s, i) => `${xs[i]},${y(s.from)}`).reverse();
    return `M${top.join(' L')} L${base.join(' L')} Z`;
  }

  _capacityPath(x, y) {
    const weeks = this._weeks;
    if (!weeks.length) return '';
    // Capacity is a weekly figure, so the line steps: flat across each band, joined at the edges.
    const parts = weeks.map((w) => {
      const x0 = x(w.week_start);
      const x1 = x0 + x.bandwidth();
      return `${x0},${y(w.capacity_hours)} L${x1},${y(w.capacity_hours)}`;
    });
    return `M${parts.join(' L')}`;
  }

  _marks = ({ x, y, plot, svg: s }) => {
    const weeks = this._weeks;
    const layers = this._layers;
    const last = weeks.at(-1);
    return s`
      <g class="layers">
        ${repeat(
          layers,
          (layer) => layer.key,
          (layer, i) => s`
            <g class="layer" data-layer=${layer.key}>
              <path d=${this._areaPath(i, x, y)} fill=${fillFor(layer.token, layer.fill)}
                    stroke="var(--surface)" stroke-width="2" stroke-linejoin="round" />
            </g>`,
        )}
      </g>
      <g class="over">
        ${weeks.map((w) =>
          w.over_hours > 0
            ? s`<rect x=${x(w.week_start)} width=${x.bandwidth()}
                     y=${y(w.demand_hours)} height=${Math.max(0, y(w.capacity_hours) - y(w.demand_hours))} rx="1"
                     fill="none" stroke="var(--status-overloaded)" stroke-width="1.5" />
                <text x=${x.centre(w.week_start)} y=${y(w.demand_hours) - 4} text-anchor="middle"
                      fill="var(--status-overloaded)" font-size="9">■</text>`
            : nothing,
        )}
      </g>
      <path class="capacity" d=${this._capacityPath(x, y)} fill="none"
            stroke="var(--ink-strong)" stroke-width="2" stroke-dasharray="5 3" stroke-linejoin="round" />
      ${
        last
          ? s`<text class="capacity-label" x=${x(last.week_start) + x.bandwidth()}
                    y=${y(last.capacity_hours) - 6} text-anchor="end"
                    fill="var(--ink-strong)" font-size="11" font-weight="600"
                    font-family="var(--font-sans)">Available capacity</text>`
          : nothing
      }
      ${
        this._hover !== null && weeks[this._hover]
          ? s`<line class="crosshair" x1=${x.centre(weeks[this._hover].week_start)}
                    x2=${x.centre(weeks[this._hover].week_start)} y1=${plot.top} y2=${plot.top + plot.height}
                    stroke="var(--ink-muted)" stroke-width="1" stroke-dasharray="2 2" pointer-events="none" />`
          : nothing
      }
      <g class="hits">
        ${weeks.map(
          (w, i) => s`
            <rect class="hit" x=${x(w.week_start) - (x.step() - x.bandwidth()) / 2} width=${x.step()}
                  y=${plot.top} height=${plot.height} fill="transparent" style="cursor: crosshair"

                  @pointermove=${(e) => this._showTooltip(e, i)}
                  @pointerleave=${() => this._hideTooltip()}
                  @focus=${(e) => this._showTooltip(e, i)}
                  @blur=${() => this._hideTooltip()}
                  tabindex="0" role="img"
                  aria-label=${`Week of ${weekLabel(w.week_start)}: ${formatHours(w.demand_hours)} demand against ${formatHours(w.capacity_hours)} capacity`} />`,
        )}
      </g>
    `;
  };

  // -------------------------------------------------------------------------------------------
  // Hover

  _showTooltip(event, index) {
    const w = this._weeks[index];
    if (!w) return;
    this._hover = index;
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const state = STATUS_STATES.find((st) => st.key === w.band);
    const rows = [...this._layers].reverse().map((layer) => ({
      label: layer.label,
      value: formatHours(w.layers[layer.key], { exact: true }),
      token: layer.token,
      fill: layer.fill,
    }));
    rows.push({ label: 'Total demand', value: formatHours(w.demand_hours, { exact: true }) });
    rows.push({
      label: 'Available capacity',
      value: formatHours(w.capacity_hours, { exact: true }),
    });
    rows.push(
      w.over_hours > 0
        ? { label: 'Over capacity', value: formatHours(w.over_hours, { exact: true }) }
        : { label: 'Headroom', value: formatHours(w.headroom_hours, { exact: true }) },
    );
    const crossing = this._layers.find((l) => l.key === w.over_layer);
    const footer = [
      w.utilization === null ? 'No capacity this week' : `${percent(w.utilization)} allocated`,
      state ? `${state.icon} ${state.label}` : null,
      crossing ? `crosses the line in ${crossing.label}` : null,
      `${count(w.people)} people · ${count(w.projects)} projects · ${count(w.opportunities)} deals`,
    ]
      .filter(Boolean)
      .join(' · ');
    tooltip.show({
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
      title: `Week of ${longDate(w.week_start)}`,
      rows,
      footer,
    });
  }

  _hideTooltip() {
    this._hover = null;
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Table

  get _columns() {
    // The specification is shared with the server's CSV (lib/layers.js); only the drawing is here.
    return withFormats(ribbonColumns({ layers: this._layers }), {
      byUnit: {
        hours: (v) => formatHours(v, { exact: true }),
        percent: (v) => percent(v),
        week: (v) => weekLabel(v),
      },
      byKey: {
        band: (v) => (v ? html`<status-badge state=${v} compact></status-badge>` : '—'),
      },
    });
  }

  get _rows() {
    return ribbonExportRows(this._weeks, this._layers);
  }

  // -------------------------------------------------------------------------------------------
  // Render

  _subtitle() {
    const d = this._data;
    const { filters } = this.appState;
    if (!d) return '';
    const who = d.entity ? `${d.entity.label} (${d.dimension})` : 'Whole company';
    const notes = [];
    if (!RIBBON_DIMENSIONS.includes(filters.dimension))
      notes.push('the ribbon has no project dimension, so the company is shown');
    if (filters.grain !== 'week') notes.push('weekly grain (the ribbon is weekly)');
    return `${who} · ${d.weeks_count} weeks from ${weekLabel(d.from)} · pipeline ${d.pipeline_mode}${
      notes.length ? ` · ${notes.join('; ')}` : ''
    }`;
  }

  _kpis() {
    const t = this._data?.totals;
    if (!t) return nothing;
    const peak = t.peak;
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${t.utilization === null ? '—' : percent(t.utilization)}</div>
          <div class="l">allocated over the window</div>
        </div>
        <div class="kpi">
          <div class="n">${peak ? percent(peak.utilization) : '—'}</div>
          <div class="l">peak${peak ? `, week of ${weekLabel(peak.week_start)}` : ''}</div>
        </div>
        <div class="kpi">
          <div class="n">${formatHours(t.over_hours)}</div>
          <div class="l">
            over capacity in ${count(t.weeks_over)} of ${this._weeks.length} weeks
          </div>
        </div>
      </div>
    `;
  }

  _legend() {
    const layers = [...this._layers].reverse(); // top of the stack first, as the eye reads it
    return html`
      <div class="legend-row">
        <chart-legend
          .items=${layers.map((l) => ({ label: l.label, token: l.token, fill: l.fill }))}
        ></chart-legend>
        <span class="line-key">
          <svg width="26" height="8" aria-hidden="true">
            <line
              x1="0"
              x2="26"
              y1="4"
              y2="4"
              stroke="var(--ink-strong)"
              stroke-width="2"
              stroke-dasharray="5 3"
            />
          </svg>
          <span>Available capacity</span>
        </span>
        <span class="line-key over-key">
          <span>■</span>
          <span class="word">Over capacity</span>
        </span>
      </div>
    `;
  }

  render() {
    const { run } = this.appState;
    const d = this._data;
    const weeks = this._weeks;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!d && this._loading) body = html`<div class="state">Loading the ribbon…</div>`;
    else if (!d || weeks.length === 0)
      body = html`<div class="state">No weeks in this window.</div>`;
    else
      body = html`
        <chart-frame
          .categories=${weeks.map((w) => w.week_start)}
          .max=${this._maxValue()}
          .marks=${this._marks}
          .formatCategory=${(c) => weekLabel(c)}
          band-padding="0"
          value-label="Hours"
          category-label="Week commencing"
          height="320"
        ></chart-frame>
        ${this._legend()}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`Weekly demand by layer against capacity — ${this._subtitle()}`}
            export-name="demand-ribbon"
            page-size="13"
            dense
            .columns=${this._columns}
            .rows=${this._rows}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Demand ribbon</div>
            <h2>When does demand cross capacity, and what pushes it over?</h2>
            <div class="sub">${this._subtitle()}</div>
          </div>
          ${this._kpis()}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('demand-ribbon', DemandRibbon);
